
import { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, Button, useTheme } from "@mui/material";
import axios from "axios";
import { tokens } from "../../theme";
import SimpleTable from "../../components/SimpleTable";
import SalesTable from "../../components/SalesTable";
import PurchaseTable from "../../components/PurchaseTable";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const ProfitLossReport = () => {
  const theme = useTheme();
  const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState({});
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/report/profit-loss`,
        payload
      );

      setSales(res.data.records.sales || []);
      setPurchases(res.data.records.purchases || []);
      setExpenses(res.data.records.expenses || []);
      setTotals(res.data.totals || {});
    } catch (err) {
      console.error(err);
      setSales([]);
      setPurchases([]);
      setExpenses([]);
      setTotals({});
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
  fetchReport();
}, [fetchReport]);

  const expenseColumns = [
    {
      field: "expenseDate",
      headerName: "Date",
      width: 120,
      valueFormatter: ({ value }) => dayjs.utc(value).format("YYYY-MM-DD"),
    },
    { field: "description", headerName: "Description", width: 300 },
    { field: "amount", headerName: "Amount", width: 120, type: "number" },
  ];
const formattedSales = sales.map((sale, index) => {
  const totalQuantity =
    sale.items?.reduce(
      (acc, i) => acc + parseFloat(i.quantity || 0),
      0
    ) || 0;

  const totalPrice =
    sale.items?.reduce(
      (acc, i) => acc + parseFloat(i.totalPrice || 0),
      0
    ) || 0;

  return {
    id: sale.id ?? index,
    date: sale.salesDate ? dayjs.utc(sale.salesDate).format("YYYY-MM-DD") : "-",
    salesman: sale.User?.fullName || "-",
    warehouse: sale.Warehouse?.name || "-",
    saleQty: totalQuantity,
    totalPrice: totalPrice.toFixed(2),
    paidAmount: Number(sale.paidAmount || 0).toFixed(2),
    customer: sale.Customer?.name || "-",
    SalesItems: sale.items || [],
  };
});

  return (
    <Box p={3}>
      <Typography variant="h4" mb={3}>
        Profit & Loss Report
      </Typography>

      {/* Date Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>



      {/* Sales Table */}
      <Typography variant="h6" mb={1}>
        Sales
      </Typography>
   <SalesTable
  rows={formattedSales}
  summary={{
    totalItemsSold: formattedSales.reduce(
      (acc, s) => acc + Number(s.saleQty || 0),
      0
    ),
    totalSales: totals.totalSales,
    totalPaid: formattedSales.reduce(
      (acc, s) => acc + Number(s.paidAmount || 0),
      0
    ),
    totalCredit: formattedSales.reduce(
      (acc, s) =>
        acc + (Number(s.totalPrice) - Number(s.paidAmount)),
      0
    ),
  }}
  themeColors={colors}
/>


      {/* Purchases Table */}
      <Typography variant="h6" mb={1} mt={3}>
        Purchases
      </Typography>
      <PurchaseTable
        rows={purchases}
        summary={{
          totalPrice: purchases.reduce(
            (acc, p) => acc + parseFloat(p.totalVatedAfterDiscount || 0),
            0
          ),
          totalVAT: purchases.reduce(
            (acc, p) => acc + parseFloat(p.vat || 0),
            0
          ),
          totalWithholding: purchases.reduce(
            (acc, p) => acc + parseFloat(p.withholdingAmount || 0),
            0
          ),
        }}
        themeColors={colors}
      />

      {/* Expenses Table */}
      <Typography variant="h6" mb={1} mt={3}>
        Expenses
      </Typography>
      <SimpleTable
        rows={expenses}
        columns={expenseColumns}
        getRowId={(row) => row.id}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        height="40vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      />

      {/* Summary */}
      <Box mt={3} p={2} sx={{ borderTop: `2px solid ${colors.grey[400]}` }}>
        <Typography variant="h6">Summary</Typography>
        <Typography>
          Total Sales: <b>{totals.totalSales ?? 0}</b>
        </Typography>
        <Typography>
          Total Purchases: <b>{totals.totalPurchase ?? 0}</b>
        </Typography>
        <Typography>
          Total Expenses: <b>{totals.totalExpense ?? 0}</b>
        </Typography>
        <Typography>
          Gross Profit:{" "}
          <b style={{ color: totals.grossProfit >= 0 ? "green" : "red" }}>
            {totals.grossProfit ?? 0}
          </b>
        </Typography>
        <Typography>
          Net Profit:{" "}
          <b style={{ color: totals.netProfit >= 0 ? "green" : "red" }}>
            {totals.netProfit ?? 0}
          </b>
        </Typography>
      </Box>
    </Box>
  );
};

export default ProfitLossReport;
